import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { Trash2, CalendarOff, Plus, X } from "lucide-react";

const fmt = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const NOTE_PRESETS = ["Closed for Holidays", "Fully Booked", "Private Event", "Staff Day Off"];

export default function AdminAvailability() {
  const [overview, setOverview] = useState({}); // date -> status
  const [blocked, setBlocked] = useState([]);   // list of saved rec
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);   // current selected day's availability

  const [fullyClosed, setFullyClosed] = useState(false);
  const [note, setNote] = useState("");
  const [ranges, setRanges] = useState([]); // [{start,end,note}]
  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");

  const loadOverview = useCallback((month) => {
    const start = new Date(month); start.setDate(1);
    const end = new Date(month); end.setMonth(end.getMonth() + 2);
    api.get("/availability/overview", { params: { from_: fmt(start), to: fmt(end) } })
      .then((r) => {
        const m = {};
        (r.data || []).forEach((it) => { m[it.date] = it.status; });
        setOverview(m);
      })
      .catch(() => {});
  }, []);

  const loadBlocked = useCallback(() => {
    api.get("/unavailable-dates").then((r) => setBlocked(r.data || []));
  }, []);

  useEffect(() => {
    loadOverview(new Date());
    loadBlocked();
  }, [loadOverview, loadBlocked]);

  const loadDetail = async (dateStr) => {
    const { data } = await api.get(`/availability/${dateStr}`);
    setDetail(data);
    setFullyClosed(!!data.fully_closed);
    setRanges(data.blocked_ranges || []);
    setNote(data.note || "");
  };

  const onDayClick = (d) => {
    if (!d) return;
    setSelected(d);
    loadDetail(fmt(d));
  };

  const addRange = () => {
    if (!newStart || !newEnd) return toast.error("Enter both start and end times");
    if (newStart >= newEnd) return toast.error("End time must be after start time");
    setRanges([...ranges, { start: newStart, end: newEnd, note: "" }]);
    setNewStart(""); setNewEnd("");
  };

  const removeRange = (i) => setRanges(ranges.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!selected) return;
    try {
      await api.post("/unavailable-dates", {
        date: fmt(selected),
        fully_closed: fullyClosed,
        note,
        blocked_ranges: fullyClosed ? [] : ranges,
      });
      toast.success("Availability saved");
      await loadBlocked();
      loadOverview(selected);
      await loadDetail(fmt(selected));
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const clearDay = async () => {
    if (!selected) return;
    if (!window.confirm("Reset this day to fully available?")) return;
    await api.delete(`/unavailable-dates/${fmt(selected)}`);
    toast.success("Day reset to available");
    await loadBlocked();
    loadOverview(selected);
    await loadDetail(fmt(selected));
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const statusOf = (d) => overview[fmt(d)];

  return (
    <div data-testid="admin-availability-page">
      <h1 className="font-serif-display text-4xl text-[#5979A9] mb-2">Reservation Availability</h1>
      <p className="text-[#4A5568] text-sm mb-7 max-w-3xl">
        Click any date to mark it fully closed or to block specific time ranges. Customers will only be able to book on available slots.
      </p>

      <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8">
        {/* CALENDAR */}
        <div className="bg-white rounded-2xl border border-[#F0EAE6] p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif-display text-xl text-[#5979A9] flex items-center gap-2"><CalendarOff size={18}/> Availability Overview</h2>
            <div className="flex items-center gap-4 text-[11px] text-[#4A5568]">
              <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500"/>Available</span>
              <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-400"/>Limited</span>
              <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#E05947]"/>Closed</span>
            </div>
          </div>
          <Calendar
            mode="single"
            selected={selected}
            onSelect={onDayClick}
            onMonthChange={(m) => loadOverview(m)}
            disabled={(d) => d < today}
            modifiers={{
              avail: (d) => d >= today && statusOf(d) === "available",
              partial: (d) => d >= today && statusOf(d) === "partial",
              closed: (d) => d >= today && statusOf(d) === "closed",
            }}
            modifiersClassNames={{
              avail: "ring-1 ring-green-500/40 text-green-700",
              partial: "ring-1 ring-yellow-500/60 text-yellow-700 bg-yellow-50",
              closed: "bg-[#E05947]/20 text-[#E05947] line-through font-semibold !opacity-100",
            }}
            className="rounded-xl border border-[#F0EAE6] p-3"
          />
        </div>

        {/* EDITOR */}
        <div className="bg-white rounded-2xl border border-[#F0EAE6] p-5" data-testid="day-editor">
          {!selected ? (
            <p className="text-sm text-[#4A5568] py-12 text-center">Select a date on the calendar to manage its availability.</p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif-display text-xl text-[#5979A9]">
                  {selected.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </h2>
                {detail?.weekly_closed && <span className="text-xs text-[#E05947] italic">Weekly closed</span>}
              </div>

              <label className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-[#FAF4F1] cursor-pointer">
                <input
                  type="checkbox"
                  checked={fullyClosed}
                  onChange={(e) => setFullyClosed(e.target.checked)}
                  data-testid="fully-closed-toggle"
                  disabled={detail?.weekly_closed}
                  className="w-4 h-4 accent-[#E05947]"
                />
                <span className="text-sm text-[#2D3748] font-medium">Fully Closed — no reservations on this day</span>
              </label>

              {!fullyClosed && !detail?.weekly_closed && (
                <div className="mb-5">
                  <h3 className="text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">Blocked time ranges</h3>
                  <div className="space-y-2 mb-3" data-testid="blocked-ranges-list">
                    {ranges.length === 0 ? (
                      <p className="text-xs text-[#4A5568]/70 italic">No blocked ranges — entire opening window available.</p>
                    ) : (
                      ranges.map((r, i) => (
                        <div key={i} className="flex items-center gap-3 bg-[#FAF4F1] rounded-lg px-3 py-2 text-sm">
                          <span className="font-mono text-[#E05947] font-semibold">{r.start} → {r.end}</span>
                          {r.note && <span className="text-xs text-[#4A5568]">· {r.note}</span>}
                          <button onClick={() => removeRange(i)} className="ml-auto text-[#E05947]" data-testid={`remove-range-${i}`}><X size={14}/></button>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="block text-[10px] uppercase tracking-wider text-[#5979A9]/70 mb-1">Start</label>
                      <input type="time" value={newStart} onChange={(e) => setNewStart(e.target.value)} data-testid="range-start" className="w-full bg-transparent border-b border-[#F0EAE6] focus:border-[#E05947] outline-none py-1.5 text-sm"/>
                    </div>
                    <div className="flex-1">
                      <label className="block text-[10px] uppercase tracking-wider text-[#5979A9]/70 mb-1">End</label>
                      <input type="time" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} data-testid="range-end" className="w-full bg-transparent border-b border-[#F0EAE6] focus:border-[#E05947] outline-none py-1.5 text-sm"/>
                    </div>
                    <button onClick={addRange} data-testid="add-range-button" className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-[#5979A9] text-white text-xs hover:bg-[#46618A]">
                      <Plus size={14}/> Add
                    </button>
                  </div>
                </div>
              )}

              {fullyClosed && (
                <div className="mb-5">
                  <h3 className="text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">Quick reason</h3>
                  <div className="flex flex-wrap gap-2">
                    {NOTE_PRESETS.map((p) => (
                      <button key={p} type="button" onClick={() => setNote(p)} className="text-xs px-3 py-1.5 rounded-full bg-[#FAF4F1] hover:bg-[#F0EAE6] text-[#5979A9]">{p}</button>
                    ))}
                  </div>
                </div>
              )}

              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Optional note (e.g. Christmas Day)"
                data-testid="day-note-input"
                className="w-full bg-transparent border-b-2 border-[#F0EAE6] focus:border-[#E05947] outline-none py-2 text-sm mb-5"
              />

              <div className="flex gap-3">
                <button onClick={save} data-testid="save-availability-button" disabled={detail?.weekly_closed} className="btn-primary !py-2.5 !px-5 !text-sm">Save changes</button>
                <button onClick={clearDay} className="btn-secondary !py-2.5 !px-5 !text-sm">Reset day</button>
              </div>

              {detail && detail.slots && detail.slots.length > 0 && (
                <div className="mt-6 pt-5 border-t border-[#F0EAE6]">
                  <h3 className="text-xs uppercase tracking-[0.2em] text-[#5979A9]/80 mb-2 font-semibold">Currently bookable slots ({detail.slots.length})</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {detail.slots.map((s) => {
                      const cls = s.status === "full" ? "bg-[#E05947]/10 text-[#E05947] border-[#E05947]/30 line-through"
                        : s.status === "limited" ? "bg-yellow-50 text-yellow-700 border-yellow-300"
                        : "bg-green-50 text-green-700 border-green-200";
                      return (
                        <span key={s.time} className={`text-xs px-2 py-1 rounded-md border ${cls}`} title={`${s.remaining} seats left`}>
                          {s.time}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* SAVED LIST */}
      <div className="mt-8">
        <h2 className="font-serif-display text-2xl text-[#5979A9] mb-4">All managed dates ({blocked.length})</h2>
        {blocked.length === 0 ? (
          <p className="text-sm text-[#4A5568] bg-white border border-[#F0EAE6] rounded-2xl p-6">No special dates configured yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="blocked-dates-list">
            {blocked.map((b) => (
              <div key={b.date} data-testid={`blocked-row-${b.date}`} className="bg-white border border-[#F0EAE6] rounded-xl p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-semibold text-[#5979A9]">
                      {new Date(b.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                    </div>
                    <div className="text-xs mt-1">
                      {b.fully_closed ? (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-[#E05947]/15 text-[#E05947] font-semibold">Fully closed</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-semibold">{(b.blocked_ranges||[]).length} range(s) blocked</span>
                      )}
                    </div>
                    {b.note && <div className="text-xs text-[#4A5568] mt-1">{b.note}</div>}
                  </div>
                  <button onClick={async () => { await api.delete(`/unavailable-dates/${b.date}`); toast.success("Reset"); loadBlocked(); loadOverview(new Date()); }}
                          className="text-[#E05947] p-1 hover:bg-[#FAF4F1] rounded-lg" data-testid={`unblock-${b.date}`}>
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
